import { BOX_PLUS_CHANNELS_GRID_ZONES, CustomShape } from '@statsbomb/kitbag-datavis';
import { FilterSchema, PlayPattern } from '@statsbomb/parachute-types';
import { gameTypeOptions } from '@/consts/gameMetrics';
import { ConfigFilterAtomValues } from '@/types/userConfigs';

const isIntegerId = (value: unknown): value is number => Number.isInteger(value);
// still admits legitimately-negative OBV values (hence finite, not integer).
const isFiniteNumber = (value: unknown): value is number => Number.isFinite(value);

const positionSchema = FilterSchema.shape.positions.unwrap().element;
const outcomeSchema = FilterSchema.shape.outcomes.unwrap().element;
const bodyPartSchema = FilterSchema.shape.bodyParts.unwrap().element;
const playPatternSchema = FilterSchema.shape.playPatterns.unwrap().element;
const eventFilterGroupSchema = FilterSchema.shape.eventFilterGroups.unwrap().element;

type EnumElementSchema = { safeParse: (value: unknown) => { success: boolean } };
const isValueOf =
  <T extends string>(schema: EnumElementSchema) =>
  (value: unknown): value is T =>
    schema.safeParse(value).success;

/** Convert agent-emitted zone IDs to the `CustomShape[]` shape the atom holds. */
const hydrateZoneIds = (ids: unknown[]): CustomShape[] => {
  const numericIds = new Set(ids.filter(isIntegerId));
  return BOX_PLUS_CHANNELS_GRID_ZONES.filter(zone => numericIds.has(zone.id));
};

export const applyFilterToolCallInput = (
  input: Record<string, unknown>,
  prev: ConfigFilterAtomValues,
): ConfigFilterAtomValues => {
  const next = { ...prev };

  if (Array.isArray(input.competitionIds)) {
    next.competitionIds = input.competitionIds.filter(isIntegerId);
  } else if (input.competitionIds === null) {
    next.competitionIds = null;
  }

  if (Array.isArray(input.seasonIds)) {
    next.seasonIds = input.seasonIds.filter(isIntegerId);
  } else if (input.seasonIds === null) {
    next.seasonIds = null;
  }

  if (Array.isArray(input.teamIds)) {
    next.teamIds = input.teamIds.filter(isIntegerId);
  }

  if (Array.isArray(input.playerIds)) {
    next.playerIds = input.playerIds.filter(isIntegerId);
  }

  if (Array.isArray(input.positions)) {
    next.positions = input.positions.filter(isValueOf(positionSchema));
  }

  if (Array.isArray(input.outcomes)) {
    next.outcomes = input.outcomes.filter(isValueOf(outcomeSchema));
  }

  if (Array.isArray(input.bodyParts)) {
    next.bodyParts = input.bodyParts.filter(isValueOf(bodyPartSchema));
  }

  if (Array.isArray(input.playPatterns)) {
    next.playPatterns = input.playPatterns.filter(isValueOf<PlayPattern>(playPatternSchema));
  }

  if (Array.isArray(input.pitchStartZones)) {
    next.pitchStartZones = hydrateZoneIds(input.pitchStartZones);
  }

  if (Array.isArray(input.pitchEndZones)) {
    next.pitchEndZones = hydrateZoneIds(input.pitchEndZones);
  }

  if (isFiniteNumber(input.minXg)) next.minXg = input.minXg;
  if (isFiniteNumber(input.maxXg)) next.maxXg = input.maxXg;
  if (isFiniteNumber(input.minObv)) next.minObv = input.minObv;
  if (isFiniteNumber(input.maxObv)) next.maxObv = input.maxObv;

  if (Array.isArray(input.matchPeriods)) {
    next.matchPeriods = input.matchPeriods.filter(isIntegerId);
  } else if (isFiniteNumber(input.minMatchMinute) || isFiniteNumber(input.maxMatchMinute)) {
    next.matchPeriods = [];
  }
  if (isFiniteNumber(input.minMatchMinute)) next.minMatchMinute = input.minMatchMinute;
  if (isFiniteNumber(input.maxMatchMinute)) next.maxMatchMinute = input.maxMatchMinute;

  if (Array.isArray(input.eventFilterGroups)) {
    next.eventFilterGroups = input.eventFilterGroups.flatMap(rawGroup => {
      const result = eventFilterGroupSchema.safeParse(rawGroup);
      if (!result.success) return [];
      const { pitchStartZones, pitchEndZones, ...rest } = result.data;
      return [
        { ...rest, pitchStartZones: hydrateZoneIds(pitchStartZones), pitchEndZones: hydrateZoneIds(pitchEndZones) },
      ];
    });
  }

  // gameType: the agent emits a plain string ('HOME' | 'AWAY' | 'HOME_AWAY');
  // the atom holds the matching TranslatedOption, so map the string to it.
  const gameTypeOption = gameTypeOptions.find(option => option.value === input.gameType);
  if (gameTypeOption) next.gameType = gameTypeOption;

  return next;
};
